import { WebsocketResponse } from 'api/services/websocketTypes';
import {
  getOrderTriggerExplanation,
  isTPSLOrder,
} from 'components/orders/orderUtil';
import { EXCHANGE_DECIMALS } from 'config/constants';
import { UnreachableCaseError } from 'lib/utils';
import { getStateOutsideOfComponent } from 'reduxState/store';
import { ReduxState } from 'reduxState/storeCore';

import {
  throttledNotificationsMsg,
  renderNotification,
} from './notificationsRender';
import { recordEventAndThrottle } from './notificationsThrottle';
import {
  displayBasicNotification,
  DISPLAYED_TIME_MS,
} from './notificationsUtils';
import { NotificationObject } from './notificationTypes';

/** Note this method also access Redux state */
export async function triggerNotificationFromWebsocketMessage(
  response: WebsocketResponse
) {
  const appState = await getStateOutsideOfComponent();
  const notificationObject = await transformNotificationsFromWebsocket(
    response,
    appState
  );
  if (!notificationObject) {
    return;
  }
  displayNotification(notificationObject);
}

/**
 * Display in-app notification. Using prevention before notification spam:
 *  On high volume of notifications, it will be temporary muted
 * Note: This function will use store to get tick and step sizes
 */
export async function displayNotification(
  notificationObject: NotificationObject
) {
  const uniqueId = getNotificationUniqueId(notificationObject);

  const check = recordEventAndThrottle(uniqueId);
  if (check === 'ignored') {
    return;
  }
  if (check === 'notify') {
    setTimeout(
      () => {
        displayBasicNotification(
          throttledNotificationsMsg,
          'too-many',
          'warning'
        );
      },
      // Wait for other notifications to disapear
      Math.round(DISPLAYED_TIME_MS * 0.7)
    );
    return;
  }

  const notification = renderNotification(notificationObject);

  displayBasicNotification(
    notification,
    uniqueId,
    getNotificationType(notificationObject),
    isAutoCloseDisabled(notificationObject)
  );
}

const isAutoCloseDisabled = (notification: NotificationObject) => {
  return (
    notification.type === 'delegatedKeyExpiringNotice' ||
    notification.type === 'delegatedKeyInvalid' ||
    notification.type === 'delegatedKeyExpired'
  );
};

const getNotificationUniqueId = (n: NotificationObject): string => {
  const { type } = n;
  switch (type) {
    case 'orderPlaced':
    case 'orderTPSLPlaced':
    case 'orderPartiallyFilled':
    case 'orderFilled':
    case 'depositCredited':
    case 'walletLiquidation':
    case 'dustPositionClose':
    case 'orderDeleverage':
    case 'orderCanceled':
    case 'orderTPSLCanceled':
      return `${n.type}${n.time}`;
    case 'orderError':
      return `${n.type}${n.baseSymbol}${n.message}`;
    case 'delegatedKeyExpired':
    case 'delegatedKeyExpiringNotice':
    case 'delegatedKeyInvalid':
    case 'payoutSuccess':
      return `${n.type}`;
    default:
      throw new UnreachableCaseError(type);
  }
};

const getNotificationType = (
  notification: NotificationObject
): 'success' | 'error' | 'warning' | undefined => {
  const { type } = notification;
  switch (type) {
    case 'orderPartiallyFilled':
    case 'orderFilled':
    case 'depositCredited':
    case 'dustPositionClose':
    case 'payoutSuccess':
      return 'success';
    case 'orderError':
    case 'walletLiquidation':
      return 'error';
    case 'orderDeleverage':
      return 'warning';
    case 'orderPlaced':
    case 'orderTPSLPlaced':
    case 'orderCanceled':
    case 'orderTPSLCanceled':
      return undefined;
    case 'delegatedKeyExpiringNotice':
    case 'delegatedKeyExpired':
    case 'delegatedKeyInvalid':
      return 'warning';
    default:
      throw new UnreachableCaseError(type);
  }
};

/**
 * It's required to be subscribed on: "deposits, orders"
 *
 * TODO: can we enforce those to be actually subscribed
 *
 * https://docs.google.com/document/d/1TKnMHsTS_neUdk3l7vB70eHxb27MemR9tdpgeU1IZJQ/edit#
 */
async function transformNotificationsFromWebsocket(
  response: WebsocketResponse,
  appState: ReduxState
): Promise<NotificationObject | undefined> {
  const { listedMarkets } = appState.markets;
  const stepSizes = listedMarkets.stepSizes;
  const tickSizes = listedMarkets.tickSizes;

  switch (response.type) {
    case 'deposits':
      if (!response.data.time) {
        return;
      }
      return {
        type: 'depositCredited',
        time: response.data.time,
        quantity: response.data.quantity,
      };

    case 'orders': {
      const order = response.data;
      if (!order.type) {
        const firstFillType = order.fills?.[0]?.type;
        switch (firstFillType) {
          case 'closure':
            return {
              type: 'dustPositionClose',
              time: order.executionTime,
              baseSymbol: order.market.split('-')?.[0],
              side: order.side,
            };
          case 'liquidation':
            return {
              type: 'walletLiquidation',
              time: order.executionTime,
              baseSymbol: order.market.split('-')?.[0],
              side: order.side,
            };
          case 'deleverage':
            return {
              type: 'orderDeleverage',
              time: order.executionTime,
              baseSymbol: order.market.split('-')?.[0],
              side: order.side,
            };
          case 'limit':
          case 'market':
            console.error('[notificationsSdk] Unexpected fill without `type`');
            return;
          default:
            throw new UnreachableCaseError(firstFillType);
        }
      }
      if (
        order.type === 'limit' ||
        order.type === 'market' ||
        order.type === 'stopLossLimit' ||
        order.type === 'stopLossMarket' ||
        order.type === 'takeProfitLimit' ||
        order.type === 'takeProfitMarket' ||
        order.type === 'trailingStopMarket'
      ) {
        const maximumPositionSize =
          appState.markets.listedMarkets.maximumPositionSizes[order.market];
        if (!maximumPositionSize) {
          console.warn('[notificationsSdk] Unknown maximum position size');
        }
        if (order.status === 'partiallyFilled') {
          return {
            type: 'orderPartiallyFilled',
            time: order.executionTime,
            baseSymbol: order.market.split('-')?.[0],
            originalQuantity: order.originalQuantity,
            executedQuantity: order.executedQuantity,
            avgExecutionPrice: order.avgExecutionPrice || '0',
            side: order.side,
            priceDecimals: tickSizes[order.market]?.visibleDecimals,
            quantityDecimals: stepSizes[order.market]?.visibleDecimals,
          };
        } else if (order.status === 'filled') {
          // TODO: for market order too ! generate while clicking...
          return {
            type: 'orderFilled',
            time: order.executionTime,
            baseSymbol: order.market.split('-')?.[0],
            quantity: order.executedQuantity,
            avgExecutionPrice: order.avgExecutionPrice || '0',
            side: order.side,
            priceDecimals: tickSizes[order.market]?.visibleDecimals,
            quantityDecimals: stepSizes[order.market]?.visibleDecimals,
          };
        } else if (
          order.status === 'canceled' &&
          !order.conditionalOrderId &&
          maximumPositionSize &&
          isTPSLOrder(order, maximumPositionSize)
        ) {
          return {
            type: 'orderTPSLCanceled',
            time: order.executionTime,
            baseSymbol: order.market.split('-')?.[0] || '?',
            orderType: order.type,
          };
        } else if (order.status === 'canceled' && !order.conditionalOrderId) {
          return {
            type: 'orderCanceled',
            time: order.executionTime,
            originalQuantity: order.originalQuantity,
            isTpSlClosure: maximumPositionSize
              ? isTPSLOrder(order, maximumPositionSize)
              : false,
            side: order.side,
            baseSymbol: order.market.split('-')?.[0] || '?',
            price: order.price,
            priceDecimals: tickSizes[order.market]?.visibleDecimals,
            quantityDecimals: stepSizes[order.market]?.visibleDecimals,
          };
        } else if (
          // Standard limit order placed into the books
          order.status === 'open' ||
          // Stop Orders - not yet placed to books
          order.status === 'active' ||
          // Conditional TP/SL order
          (order.status === 'inactive' && order.conditionalOrderId)
        ) {
          const appState = await getStateOutsideOfComponent();
          const marketTickSizeDecimals =
            appState.markets.listedMarkets.tickSizes[order.market]
              ?.visibleDecimals;
          const maximumPositionSize =
            appState.markets.listedMarkets.maximumPositionSizes[order.market];
          const tpSLClosure = maximumPositionSize
            ? isTPSLOrder(order, maximumPositionSize)
            : undefined;
          if (tpSLClosure) {
            return {
              type: 'orderTPSLPlaced',
              time: order.executionTime,
              baseSymbol: order.market.split('-')?.[0] || '?',
              side: order.side,
              trigger: getOrderTriggerExplanation(
                order,
                marketTickSizeDecimals ?? EXCHANGE_DECIMALS
              ),
            };
          }
          return {
            type: 'orderPlaced',
            time: order.executionTime,
            originalQuantity: order.originalQuantity,
            baseSymbol: order.market.split('-')?.[0] || '?',
            side: order.side,
            price: order.price,
            trigger: getOrderTriggerExplanation(
              order,
              marketTickSizeDecimals ?? EXCHANGE_DECIMALS
            ),
            priceDecimals: tickSizes[order.market]?.visibleDecimals,
            quantityDecimals: stepSizes[order.market]?.visibleDecimals,
          };
        }
        if (order.errorMessage) {
          return {
            type: 'orderError',
            message: order.errorMessage,
            time: Date.now(),
            baseSymbol: order.market.split('-')?.[0],
          };
        }
      }
      break;
    }
    case 'webclient':
      if (
        response.data.event === 'transaction_settled' &&
        response.data.action === 'payout'
      ) {
        return {
          type: 'payoutSuccess',
          time: Date.now(),
        };
      }
      break;
  }
}
